import { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Button,
  Card,
  Container,
  Input,
  NavigationButton,
  OptionButton,
  SmallOkButton,
  Text,
} from "../components/StyledComponents";
import perguntasData from "../data/perguntas.json";
import RatingScale from "../components/RatingScale";
import OptionWithTooltip from "../components/OptionWithTooltip";
import LabelWithTooltip from "../components/LabelWithTooltip";
import { submitDiagnosis } from "../utils/submitDiagnosis";
import { buildDiagnosisPayload } from "../utils/buildDiagnosisPayload";
import { fetchCnpjInfo } from "../utils/cnpj";
import { getSurveyOrigem, getSurveyStartedAt, captureSurveyOrigem, getSurveyPrivacyAccepted, getSurveyPrivacyAcceptedAt } from "../utils/surveySession";
import { formatCelularDisplay } from "../utils/inputMasks";

const SurveyContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    214deg,
    rgba(8, 77, 110, 1) 0%,
    rgba(158, 38, 128, 1) 100%
  );
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 64px;

  @media (max-width: 768px) {
    padding-bottom: 56px;
  }
`;

const SurveyPageContainer = styled(Container)`
  width: 100%;
  padding: 20px;

  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const SurveyCard = styled(Card)`
  width: 750px;
  min-height: 512px;
  max-height: calc(100vh - 80px);
  margin: 0 auto;
  position: relative;
  z-index: 1;
  background: #fff;
  color: #222;
  box-shadow: 0 8px 48px 0 rgba(8, 77, 110, 0.13);
  border-radius: 16px;
  border: none;
  padding: 18px 22px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 6px;
  overflow: hidden;
  transition: min-height 0.3s ease;

  @media (max-width: 900px) {
    width: 100%;
    min-width: 0;
    min-height: 320px;
    max-height: calc(100vh - 48px);
  }

  @media (max-width: 480px) {
    min-width: 0;
    min-height: 220px;
    padding: 14px 16px;
  }
`;

const QuestionBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
`;

const QuestionNumber = styled.div`
  font-size: 0.95rem;
  color: #084d6e;
  font-weight: 600;
  flex-shrink: 0;
`;

const QuestionText = styled.h2`
  font-size: 1.65rem;
  font-weight: 600;
  color: #6d2170;
  line-height: 1.3;
  margin: 0 0 4px;

  @media (max-width: 768px) {
    font-size: 1.35rem;
  }
`;

const RequiredIndicator = styled.span`
  color: #ffeb3b;
  margin-left: 5px;
`;

const OptionsContainer = styled.div`
  margin: 0px 0;
`;

const InlineOptionsContainer = styled(OptionsContainer)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

const InlineOptionButton = styled(OptionButton)`
  margin: 0;
  white-space: nowrap;
  padding: 8px 12px;
  font-size: 13px;

  @media (max-width: 480px) {
    white-space: normal;
    text-align: center;
    line-height: 1.35;
  }
`;

const BlockContainer = styled.div`
  & + & {
    margin-top: 10px;
  }

  opacity: ${(props) => (props.$disabled ? 0.45 : 1)};
  pointer-events: ${(props) => (props.$disabled ? "none" : "auto")};
  transition: opacity 0.2s ease;
`;

const InlineFieldsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
`;

const InlineField = styled.div`
  flex: 1;
  min-width: 140px;
  max-width: 200px;
  opacity: ${(props) => (props.$disabled ? 0.45 : 1)};
  pointer-events: ${(props) => (props.$disabled ? "none" : "auto")};

  @media (max-width: 480px) {
    flex: 1 1 100%;
    min-width: 0;
    max-width: none;
  }
`;

const BlockLabel = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #084d6e;
  margin-bottom: 4px;
`;

const GroupBlockLabel = styled(BlockLabel)`
  margin-bottom: 28px;
`;

const BlockLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
`;

const MultiSelectTag = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9e2480;
  background: #fce4f3;
  border: 1px solid #e8b8dc;
  border-radius: 4px;
  padding: 2px 8px;
  line-height: 1.4;
`;

const MultiSelectHint = ({ label }) => (
  <BlockLabelRow>
    {label && <BlockLabel style={{ marginBottom: 0 }}>{label}</BlockLabel>}
    <MultiSelectTag>Multiseleção</MultiSelectTag>
  </BlockLabelRow>
);

const BlockOptionButton = styled(OptionButton)`
  margin: 3px;
  padding: 10px 16px;
  font-size: 14px;
`;

const EscalaComOpcaoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
  margin: 8px 0;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

const NumeroComOpcaoInput = styled(Input)`
  width: 30%;
  max-width: 180px;

  @media (max-width: 480px) {
    width: 100%;
    max-width: none;
  }
`;

const SurveyWideInput = styled(Input)`
  width: 60%;
  max-width: 420px;

  @media (max-width: 480px) {
    width: 100%;
    max-width: none;
  }
`;

const SurveyMediumInput = styled(Input)`
  width: 70%;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const SurveyHalfInput = styled(Input)`
  width: 50%;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const SurveyShortInput = styled(Input)`
  width: 40%;
  max-width: 220px;

  @media (max-width: 480px) {
    width: 100%;
    max-width: none;
  }
`;

const SurveyCompactInput = styled(Input)`
  width: 30%;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const InlineNumeroInput = styled(Input)`
  width: 100%;
  max-width: 180px;

  @media (max-width: 480px) {
    max-width: none;
  }
`;

const EscalaOpcaoExclusiva = styled(BlockOptionButton)`
  flex-shrink: 0;
  margin-left: auto;

  @media (max-width: 480px) {
    margin-left: 0;
    width: 100%;
    text-align: center;
  }
`;

const ValidatedEmailInput = styled(Input)`
  width: 60%;
  max-width: 420px;
  background: ${(props) => (props.$valid ? "#e8f5e9" : "#fff")};
  border-color: ${(props) => (props.$valid ? "#a5d6a7" : "#ddd")};

  @media (max-width: 480px) {
    width: 100%;
    max-width: none;
  }

  &:focus {
    border-color: ${(props) => (props.$valid ? "#81c784" : "#9e2480")};
    box-shadow: ${(props) =>
      props.$valid
        ? "0 0 0 2px rgba(129, 199, 132, 0.35)"
        : "0 0 0 2px rgba(158, 36, 128, 0.2)"};
  }
`;

const ExclusiveOptionsContainer = styled(OptionsContainer)`
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #e5e7eb;
`;

const InputContainer = styled.div`
  margin: 0px 0;
`;

const SubmitActionsContainer = styled.div`
  margin-top: 8px;
  flex-shrink: 0;
`;

const NavigationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0 0;
  margin-top: auto;
  flex-shrink: 0;
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const ConditionalField = styled.div`
  margin-top: 15px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  border-left: 4px solid rgba(255, 255, 255, 0.3);
`;

const ConditionalFieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const BrandFooterText = styled.div`
  position: fixed;
  right: 32px;
  bottom: 24px;
  background: linear-gradient(214deg, #084d6e 0%, #9e2480 100%);
  color: #fff;
  padding: 10px 28px;
  border-radius: 22px;
  font-size: 15px;
  font-weight: 500;
  z-index: 100;
  box-shadow: 0 2px 12px rgba(8, 77, 110, 0.1);

  @media (max-width: 768px) {
    right: 12px;
    bottom: 12px;
    padding: 8px 16px;
    font-size: 13px;
  }
`;

const SuccessScreen = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 12px 8px 8px;
  max-width: 520px;
  margin: 0 auto;
`;

const SuccessEmoji = styled.div`
  font-size: 3.25rem;
  line-height: 1;
  margin-bottom: 18px;
  filter: drop-shadow(0 4px 10px rgba(158, 36, 128, 0.18));
`;

const SuccessTitle = styled.h2`
  font-family: "Oxanium", sans-serif;
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 18px;
  background: linear-gradient(135deg, #084d6e 0%, #9e2480 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 1.45rem;
  }
`;

const SuccessMessage = styled.p`
  font-family: "Oxanium", sans-serif;
  font-size: 1.08rem;
  font-weight: 400;
  line-height: 1.75;
  color: #3d4f5c;
  margin: 0;
  letter-spacing: 0.01em;

  strong {
    font-weight: 700;
    color: #084d6e;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const SuccessDivider = styled.div`
  width: 64px;
  height: 4px;
  border-radius: 999px;
  margin: 22px 0 20px;
  background: linear-gradient(90deg, #084d6e 0%, #9e2480 100%);
  opacity: 0.85;
`;

const SurveyPage = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [conditionalAnswers, setConditionalAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [stepCompletedAt, setStepCompletedAt] = useState({});
  const [isPortrait, setIsPortrait] = useState(false);
  const [pinnedTooltip, setPinnedTooltip] = useState(null);

  const perguntas = perguntasData.perguntas;
  const totalQuestions = perguntas.length;

  useEffect(() => {
    setPinnedTooltip(null);
  }, [currentQuestion]);

  useEffect(() => {
    captureSurveyOrigem(window.location.search);
  }, []);

  useEffect(() => {
    const checkOrientation = () =>
      setIsPortrait(window.innerHeight > window.innerWidth);
    checkOrientation();
    window.addEventListener("resize", checkOrientation);
    window.addEventListener("orientationchange", checkOrientation);
    return () => {
      window.removeEventListener("resize", checkOrientation);
      window.removeEventListener("orientationchange", checkOrientation);
    };
  }, []);

  const handleAnswer = (questionId, answer) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
    const question = perguntas.find((q) => q.id === questionId);

    // Pergunta escolha / escala → avança automaticamente
    if (question?.tipo === "escolha" || question?.tipo === "escala") {
      setTimeout(() => handleNext(), 300);
      return;
    }

    if (
      question &&
      question.tipo === "boolean" &&
      !question.condicao
    ) {
      setTimeout(() => handleNext(), 300);
    }
  };

  const handleConditionalAnswer = (questionId, answer) => {
    setConditionalAnswers((prev) => ({ ...prev, [questionId]: answer }));
  };

  const markStepCompleted = (questionId) => {
    setStepCompletedAt((prev) => {
      if (prev[questionId]) return prev;
      return { ...prev, [questionId]: new Date().toISOString() };
    });
  };

  const isQuestionAnswered = (question) => {
    const answer = answers[question.id];

    if (question.condicao) {
      if (!answer) return false;
      if (answer === question.condicao.se) {
        const condAnswer = conditionalAnswers[question.id];
        const additional = question.condicao.pergunta_adicional;
        if (additional?.obrigatoria) {
          return condAnswer !== undefined && String(condAnswer).trim() !== "";
        }
      }
      return true;
    }

    if (question.tipo === "multiplos_blocos") {
      const blockAnswers = answer && typeof answer === "object" ? answer : {};
      const exclusiveBlock = (question.blocos || []).find((bloco) => bloco.exclusiva);

      if (exclusiveBlock && blockAnswers[exclusiveBlock.id]) {
        return true;
      }

      if (question.validacao_blocos === "qualquer_um") {
        return (question.blocos || [])
          .filter((bloco) => !bloco.exclusiva)
          .some((bloco) => {
            const val = blockAnswers[bloco.id];
            if (bloco.tipo === "multipla_escolha") {
              return Array.isArray(val) && val.length > 0;
            }
            if (bloco.tipo === "escala") {
              return val !== undefined && val !== null && val !== "";
            }
            if (bloco.tipo === "numero" && bloco.valorPadrao !== undefined) {
              return true;
            }
            return !!val;
          });
      }

      return (question.blocos || [])
        .filter((bloco) => !bloco.exclusiva)
        .every((bloco) => {
          if (!bloco.obrigatoria) return true;

          if (
            question.id === 19 &&
            bloco.id === "satisfacao_adocao_ia" &&
            Array.isArray(blockAnswers.estamos_usando_para) &&
            blockAnswers.estamos_usando_para.includes("nao_adotamos_ia")
          ) {
            return true;
          }

          const val = blockAnswers[bloco.id];
          if (bloco.tipo === "multipla_escolha") {
            return Array.isArray(val) && val.length > 0;
          }
          if (bloco.tipo === "escala") {
            return val !== undefined && val !== null && val !== "";
          }
          if (bloco.tipo === "numero" && bloco.valorPadrao !== undefined) {
            return true;
          }
          return !!val;
        });
    }
    if (question.tipo === "multipla_escolha") {
      return Array.isArray(answer) && answer.length > 0;
    }
    if (question.tipo === "escala") {
      return answer !== undefined && answer !== null && answer !== "";
    }
    if (question.tipo === "email") {
      if (!answer) return !question.obrigatoria;
      return isValidEmail(answer);
    }
    return !!answer;
  };

  const canAdvanceFromQuestion = (question) => {
    if (!question) return false;
    if (question.id === 27) return true;
    return isQuestionAnswered(question);
  };

  const handleBlockAnswer = (question, blockId, value) => {
    const questionId = question.id;
    const bloco = (question.blocos || []).find((b) => b.id === blockId);
    const exclusiveBlockIds = (question.blocos || [])
      .filter((b) => b.exclusiva)
      .map((b) => b.id);

    setAnswers((prev) => {
      const current =
        prev[questionId] && typeof prev[questionId] === "object"
          ? prev[questionId]
          : {};

      if (bloco?.exclusiva) {
        const alreadySelected = current[blockId] === value;
        return {
          ...prev,
          [questionId]: alreadySelected ? {} : { [blockId]: value },
        };
      }

      const next = { ...current, [blockId]: value };
      exclusiveBlockIds.forEach((id) => delete next[id]);
      return { ...prev, [questionId]: next };
    });
  };

  const handleBlockMultiChoiceToggle = (question, bloco, valor) => {
    const questionId = question.id;
    const blockId = bloco.id;
    const exclusiveBlockIds = (question.blocos || [])
      .filter((b) => b.exclusiva)
      .map((b) => b.id);
    const exclusiveValues = (bloco.opcoes || [])
      .filter((o) => o.exclusiva)
      .map((o) => o.valor);

    setAnswers((prev) => {
      const current =
        prev[questionId] && typeof prev[questionId] === "object"
          ? prev[questionId]
          : {};
      const blockCurrent = Array.isArray(current[blockId])
        ? current[blockId]
        : [];

      let nextBlock;
      if (exclusiveValues.includes(valor)) {
        nextBlock = blockCurrent.includes(valor) ? [] : [valor];
      } else {
        const withoutExclusive = blockCurrent.filter(
          (v) => !exclusiveValues.includes(v)
        );
        nextBlock = withoutExclusive.includes(valor)
          ? withoutExclusive.filter((v) => v !== valor)
          : [...withoutExclusive, valor];
      }

      const next = { ...current, [blockId]: nextBlock };
      exclusiveBlockIds.forEach((id) => delete next[id]);
      return { ...prev, [questionId]: next };
    });
  };

  const handleMultiChoiceToggle = (question, valor) => {
    const exclusiveValues = (question.opcoes || [])
      .filter((o) => o.exclusiva)
      .map((o) => o.valor);

    setAnswers((prev) => {
      const current = Array.isArray(prev[question.id]) ? prev[question.id] : [];

      if (exclusiveValues.includes(valor)) {
        return {
          ...prev,
          [question.id]: current.includes(valor) ? [] : [valor],
        };
      }

      const withoutExclusive = current.filter((v) => !exclusiveValues.includes(v));
      const next = withoutExclusive.includes(valor)
        ? withoutExclusive.filter((v) => v !== valor)
        : [...withoutExclusive, valor];

      return { ...prev, [question.id]: next };
    });
  };

  const handleNext = () => {
    const question = perguntas[currentQuestion];
    if (!canAdvanceFromQuestion(question)) return;

    markStepCompleted(question.id);

    if (currentQuestion < totalQuestions - 1)
      setCurrentQuestion((prev) => prev + 1);
    else handleSubmit();
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) setCurrentQuestion((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    const unansweredRequired = perguntas.filter(
      (q) => q.obrigatoria && !isQuestionAnswered(q)
    );
    if (unansweredRequired.length > 0) {
      const firstUnanswered = unansweredRequired[0];
      const questionIndex = perguntas.findIndex(
        (q) => q.id === firstUnanswered.id
      );
      setCurrentQuestion(questionIndex);
      return;
    }

    const invalidOptionalEmail = perguntas.find(
      (q) =>
        q.tipo === "email" &&
        !q.obrigatoria &&
        answers[q.id] &&
        !isValidEmail(answers[q.id])
    );
    if (invalidOptionalEmail) {
      const questionIndex = perguntas.findIndex(
        (q) => q.id === invalidOptionalEmail.id
      );
      setCurrentQuestion(questionIndex);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    const submittedAt = new Date().toISOString();
    const lastQuestion = perguntas[totalQuestions - 1];
    const completionTimes = { ...stepCompletedAt };
    if (!completionTimes[lastQuestion.id]) {
      completionTimes[lastQuestion.id] = submittedAt;
    }

    const payload = buildDiagnosisPayload({
      perguntas,
      answers,
      conditionalAnswers,
      stepCompletedAt: completionTimes,
      submittedAt,
      inicioEm: getSurveyStartedAt(),
      origem: getSurveyOrigem(),
      cnpjData,
      aceitePoliticaPrivacidade: getSurveyPrivacyAccepted(),
      aceitePoliticaPrivacidadeEm: getSurveyPrivacyAcceptedAt(),
    });

    try {
      await submitDiagnosis(payload);
      setStepCompletedAt(completionTimes);
      setSubmitted(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Não foi possível solicitar o diagnóstico."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, "");
    return numbers
      .replace(/(\d{2})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .substring(0, 18);
  };

  const isValidEmail = (value) => {
    if (!value || typeof value !== "string") return false;
    const trimmed = value.trim();
    const atIndex = trimmed.indexOf("@");
    if (atIndex <= 0) return false;

    const local = trimmed.slice(0, atIndex);
    const domain = trimmed.slice(atIndex + 1);
    if (!local || !domain || !domain.includes(".")) return false;

    const dotIndex = domain.indexOf(".");
    return dotIndex > 0 && dotIndex < domain.length - 1;
  };

  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cnpjData, setCnpjData] = useState(null);
  const [cnpjError, setCnpjError] = useState(null);

  const fetchCNPJData = async (rawValue) => {
    setCnpjLoading(true);
    setCnpjError(null);
    setCnpjData(null);

    try {
      const data = await fetchCnpjInfo(rawValue);
      setCnpjData(data);
    } catch (error) {
      setCnpjError(
        error instanceof Error
          ? error.message
          : "Não foi possível obter dados do CNPJ agora. Tente novamente."
      );
    } finally {
      setCnpjLoading(false);
    }
  };

  const renderQuestion = (question) => {
    const currentAnswer = answers[question.id];
    const conditionalAnswer = conditionalAnswers[question.id];

    // --- PERGUNTA escolha (Força / Fraqueza) ---
    if (question.tipo === "escolha") {
      return (
        <OptionsContainer>
          {["Força", "Fraqueza"].map((opcao) => (
            <OptionButton
              key={opcao}
              selected={currentAnswer === opcao}
              onClick={() => handleAnswer(question.id, opcao)}
            >
              {opcao}
            </OptionButton>
          ))}
        </OptionsContainer>
      );
    }

    if (question.tipo === "unica_escolha") {
      const tooltipKey = (valor) => `${question.id}:${valor}`;
      const regularOpcoes = question.opcoes.filter((opcao) => !opcao.exclusiva);
      const exclusiveOpcoes = question.opcoes.filter((opcao) => opcao.exclusiva);

      const renderOption = (opcao) => {
        if (opcao.tooltip) {
          return (
            <OptionWithTooltip
              key={opcao.valor}
              opcao={opcao}
              selected={currentAnswer === opcao.valor}
              onSelect={() => handleAnswer(question.id, opcao.valor)}
              pinned={pinnedTooltip === tooltipKey(opcao.valor)}
              onTogglePin={() =>
                setPinnedTooltip((prev) =>
                  prev === tooltipKey(opcao.valor)
                    ? null
                    : tooltipKey(opcao.valor)
                )
              }
            />
          );
        }

        return (
          <OptionButton
            key={opcao.valor}
            selected={currentAnswer === opcao.valor}
            onClick={() => handleAnswer(question.id, opcao.valor)}
          >
            {opcao.texto}
          </OptionButton>
        );
      };

      return (
        <>
          <OptionsContainer>{regularOpcoes.map(renderOption)}</OptionsContainer>
          {exclusiveOpcoes.length > 0 && (
            <ExclusiveOptionsContainer>
              {exclusiveOpcoes.map(renderOption)}
            </ExclusiveOptionsContainer>
          )}
        </>
      );
    }

    if (question.tipo === "multipla_escolha") {
      const selected = Array.isArray(currentAnswer) ? currentAnswer : [];
      const tooltipKey = (valor) => `${question.id}:${valor}`;
      const regularOpcoes = question.opcoes.filter((opcao) => !opcao.exclusiva);
      const exclusiveOpcoes = question.opcoes.filter((opcao) => opcao.exclusiva);

      const renderOption = (opcao) => {
        if (opcao.tooltip) {
          return (
            <OptionWithTooltip
              key={opcao.valor}
              opcao={opcao}
              selected={selected.includes(opcao.valor)}
              onSelect={() => handleMultiChoiceToggle(question, opcao.valor)}
              pinned={pinnedTooltip === tooltipKey(opcao.valor)}
              onTogglePin={() =>
                setPinnedTooltip((prev) =>
                  prev === tooltipKey(opcao.valor)
                    ? null
                    : tooltipKey(opcao.valor)
                )
              }
            />
          );
        }

        return (
          <OptionButton
            key={opcao.valor}
            selected={selected.includes(opcao.valor)}
            onClick={() => handleMultiChoiceToggle(question, opcao.valor)}
          >
            {opcao.texto}
          </OptionButton>
        );
      };

      return (
        <>
          <MultiSelectHint />
          <OptionsContainer>{regularOpcoes.map(renderOption)}</OptionsContainer>
          {exclusiveOpcoes.length > 0 && (
            <ExclusiveOptionsContainer>
              {exclusiveOpcoes.map(renderOption)}
            </ExclusiveOptionsContainer>
          )}
        </>
      );
    }

    if (question.tipo === "multiplos_blocos") {
      const blockAnswers =
        currentAnswer && typeof currentAnswer === "object" ? currentAnswer : {};
      const exclusiveBlock = (question.blocos || []).find(
        (bloco) => bloco.exclusiva
      );
      const exclusiveSelected =
        exclusiveBlock && !!blockAnswers[exclusiveBlock.id];
      const naoAdotamosIA =
        question.id === 19 &&
        Array.isArray(blockAnswers.estamos_usando_para) &&
        blockAnswers.estamos_usando_para.includes("nao_adotamos_ia");
      const regularBlocos = (question.blocos || []).filter(
        (bloco) => !bloco.exclusiva
      );
      const exclusiveBlocos = (question.blocos || []).filter(
        (bloco) => bloco.exclusiva
      );

      const getNumeroFieldValue = (bloco, rawSelected) => {
        if (
          rawSelected !== undefined &&
          rawSelected !== null &&
          rawSelected !== ""
        ) {
          return rawSelected;
        }
        if (bloco.valorPadrao !== undefined && bloco.valorPadrao !== null) {
          return String(bloco.valorPadrao);
        }
        return "";
      };

      const renderBlock = (bloco) => {
        const selected = blockAnswers[bloco.id];
        const OptionsWrapper =
          bloco.layout === "linha"
            ? InlineOptionsContainer
            : OptionsContainer;
        const blockDisabled =
          (exclusiveSelected && !bloco.exclusiva) ||
          (naoAdotamosIA && bloco.id === "satisfacao_adocao_ia");

        if (bloco.tipo === "numero") {
          const opcaoExclusiva = bloco.opcaoExclusiva;
          const exclusivaSelecionada =
            opcaoExclusiva && selected === opcaoExclusiva.valor;
          const inputDisabled = blockDisabled || exclusivaSelecionada;

          return (
            <BlockContainer key={bloco.id} $disabled={blockDisabled}>
              {bloco.texto && <BlockLabel>{bloco.texto}</BlockLabel>}
              {opcaoExclusiva ? (
                <EscalaComOpcaoRow>
                  <NumeroComOpcaoInput
                    type="number"
                    placeholder={bloco.placeholder || "Digite sua resposta"}
                    value={
                      exclusivaSelecionada
                        ? ""
                        : getNumeroFieldValue(bloco, selected)
                    }
                    disabled={inputDisabled}
                    onChange={(e) =>
                      handleBlockAnswer(question, bloco.id, e.target.value)
                    }
                  />
                  <EscalaOpcaoExclusiva
                    selected={exclusivaSelecionada}
                    disabled={blockDisabled}
                    onClick={() =>
                      handleBlockAnswer(
                        question,
                        bloco.id,
                        exclusivaSelecionada ? "" : opcaoExclusiva.valor
                      )
                    }
                  >
                    {opcaoExclusiva.texto}
                  </EscalaOpcaoExclusiva>
                </EscalaComOpcaoRow>
              ) : (
                <NumeroComOpcaoInput
                  type="number"
                  placeholder={bloco.placeholder || "Digite sua resposta"}
                  value={getNumeroFieldValue(bloco, selected)}
                  disabled={blockDisabled}
                  onChange={(e) =>
                    handleBlockAnswer(question, bloco.id, e.target.value)
                  }
                />
              )}
            </BlockContainer>
          );
        }

        if (bloco.tipo === "multipla_escolha") {
          const blockSelected = Array.isArray(selected) ? selected : [];
          const exclusiveValues = (bloco.opcoes || [])
            .filter((o) => o.exclusiva)
            .map((o) => o.valor);
          const exclusiveSelectedInBlock = blockSelected.some((v) =>
            exclusiveValues.includes(v)
          );
          const tooltipKey = (valor) => `${question.id}:${bloco.id}:${valor}`;

          return (
            <BlockContainer key={bloco.id} $disabled={blockDisabled}>
              <MultiSelectHint label={bloco.texto} />
              <OptionsWrapper>
                {bloco.opcoes.map((opcao) => {
                  const ButtonComponent =
                    bloco.layout === "linha"
                      ? InlineOptionButton
                      : BlockOptionButton;
                  const optionDisabled =
                    blockDisabled ||
                    (exclusiveSelectedInBlock && !opcao.exclusiva);

                  if (opcao.tooltip) {
                    return (
                      <OptionWithTooltip
                        key={opcao.valor}
                        opcao={opcao}
                        selected={blockSelected.includes(opcao.valor)}
                        disabled={optionDisabled}
                        ButtonComponent={ButtonComponent}
                        onSelect={() =>
                          handleBlockMultiChoiceToggle(
                            question,
                            bloco,
                            opcao.valor
                          )
                        }
                        pinned={pinnedTooltip === tooltipKey(opcao.valor)}
                        onTogglePin={() =>
                          setPinnedTooltip((prev) =>
                            prev === tooltipKey(opcao.valor)
                              ? null
                              : tooltipKey(opcao.valor)
                          )
                        }
                      />
                    );
                  }

                  return (
                    <ButtonComponent
                      key={opcao.valor}
                      selected={blockSelected.includes(opcao.valor)}
                      disabled={optionDisabled}
                      onClick={() =>
                        handleBlockMultiChoiceToggle(question, bloco, opcao.valor)
                      }
                    >
                      {opcao.texto}
                    </ButtonComponent>
                  );
                })}
              </OptionsWrapper>
            </BlockContainer>
          );
        }

        if (bloco.tipo === "texto") {
          return (
            <BlockContainer key={bloco.id} $disabled={blockDisabled}>
              {bloco.texto && <BlockLabel>{bloco.texto}</BlockLabel>}
              <SurveyWideInput
                type="text"
                placeholder={bloco.placeholder || "Digite sua resposta"}
                value={selected || ""}
                disabled={blockDisabled}
                maxLength={bloco.maxLength}
                onChange={(e) =>
                  handleBlockAnswer(question, bloco.id, e.target.value)
                }
              />
            </BlockContainer>
          );
        }

        if (bloco.tipo === "celular") {
          return (
            <BlockContainer key={bloco.id} $disabled={blockDisabled}>
              {bloco.texto && <BlockLabel>{bloco.texto}</BlockLabel>}
              <SurveyShortInput
                type="tel"
                inputMode="numeric"
                placeholder={bloco.placeholder || "(11) 99999-9999"}
                value={selected || ""}
                disabled={blockDisabled}
                onChange={(e) =>
                  handleBlockAnswer(
                    question,
                    bloco.id,
                    formatCelularDisplay(e.target.value)
                  )
                }
              />
            </BlockContainer>
          );
        }

        if (bloco.tipo === "unica_escolha") {
          return (
            <BlockContainer key={bloco.id} $disabled={blockDisabled}>
              {bloco.texto && <BlockLabel>{bloco.texto}</BlockLabel>}
              <OptionsWrapper>
                {bloco.opcoes.map((opcao) => {
                  const ButtonComponent =
                    bloco.layout === "linha"
                      ? InlineOptionButton
                      : BlockOptionButton;

                  return (
                    <ButtonComponent
                      key={opcao.valor}
                      selected={selected === opcao.valor}
                      onClick={() =>
                        handleBlockAnswer(question, bloco.id, opcao.valor)
                      }
                    >
                      {opcao.texto}
                    </ButtonComponent>
                  );
                })}
              </OptionsWrapper>
            </BlockContainer>
          );
        }

        if (bloco.tipo === "escala") {
          const opcaoExclusiva = bloco.opcaoExclusiva;
          const exclusivaSelecionada =
            opcaoExclusiva && selected === opcaoExclusiva.valor;
          const escalaDesabilitada = blockDisabled || exclusivaSelecionada;

          return (
            <BlockContainer key={bloco.id} $disabled={blockDisabled}>
              {bloco.texto && <BlockLabel>{bloco.texto}</BlockLabel>}
              {opcaoExclusiva ? (
                <EscalaComOpcaoRow>
                  <RatingScale
                    max={bloco.max ?? 5}
                    value={selected}
                    disabled={escalaDesabilitada}
                    inline
                    onChange={(score) =>
                      handleBlockAnswer(question, bloco.id, score)
                    }
                    name={`escala-${question.id}-${bloco.id}`}
                  />
                  <EscalaOpcaoExclusiva
                    selected={exclusivaSelecionada}
                    disabled={blockDisabled}
                    onClick={() =>
                      handleBlockAnswer(
                        question,
                        bloco.id,
                        exclusivaSelecionada ? "" : opcaoExclusiva.valor
                      )
                    }
                  >
                    {opcaoExclusiva.texto}
                  </EscalaOpcaoExclusiva>
                </EscalaComOpcaoRow>
              ) : (
                <RatingScale
                  max={bloco.max ?? 5}
                  value={selected}
                  disabled={blockDisabled}
                  inline
                  onChange={(score) =>
                    handleBlockAnswer(question, bloco.id, score)
                  }
                  name={`escala-${question.id}-${bloco.id}`}
                />
              )}
            </BlockContainer>
          );
        }

        return null;
      };

      const renderNumeroInline = (bloco) => {
        const selected = blockAnswers[bloco.id];
        const blockDisabled =
          (exclusiveSelected && !bloco.exclusiva) ||
          (naoAdotamosIA && bloco.id === "satisfacao_adocao_ia");
        const tooltipKey = `bloco:${question.id}:${bloco.id}`;

        return (
          <InlineField key={bloco.id} $disabled={blockDisabled}>
            {bloco.texto &&
              (bloco.tooltip ? (
                <LabelWithTooltip
                  label={bloco.texto}
                  tooltip={bloco.tooltip}
                  pinned={pinnedTooltip === tooltipKey}
                  onTogglePin={() =>
                    setPinnedTooltip((prev) =>
                      prev === tooltipKey ? null : tooltipKey
                    )
                  }
                />
              ) : (
                <BlockLabel>{bloco.texto}</BlockLabel>
              ))}
            <InlineNumeroInput
              type="number"
              min={bloco.valorPadrao !== undefined ? 0 : undefined}
              placeholder={bloco.placeholder || "Digite sua resposta"}
              value={getNumeroFieldValue(bloco, selected)}
              disabled={blockDisabled}
              onChange={(e) =>
                handleBlockAnswer(question, bloco.id, e.target.value)
              }
            />
          </InlineField>
        );
      };

      const renderRegularBlocos = () => {
        const nodes = [];
        let idx = 0;

        while (idx < regularBlocos.length) {
          const bloco = regularBlocos[idx];

          if (bloco.tipo === "numero" && bloco.grupo) {
            const groupBlocos = [bloco];
            let next = idx + 1;

            while (
              next < regularBlocos.length &&
              regularBlocos[next].grupo === bloco.grupo &&
              regularBlocos[next].tipo === "numero"
            ) {
              groupBlocos.push(regularBlocos[next]);
              next++;
            }

            nodes.push(
              <BlockContainer key={`grupo-${bloco.grupo}`}>
                {bloco.grupoLabel && (
                  <GroupBlockLabel>{bloco.grupoLabel}</GroupBlockLabel>
                )}
                <InlineFieldsRow>
                  {groupBlocos.map(renderNumeroInline)}
                </InlineFieldsRow>
              </BlockContainer>
            );
            idx = next;
            continue;
          }

          nodes.push(renderBlock(bloco));
          idx++;
        }

        return nodes;
      };

      return (
        <>
          {renderRegularBlocos()}
          {exclusiveBlocos.length > 0 && (
            <ExclusiveOptionsContainer>
              {exclusiveBlocos.map(renderBlock)}
            </ExclusiveOptionsContainer>
          )}
        </>
      );
    }

    if (question.tipo === "escala") {
      return (
        <RatingScale
          max={question.max ?? 5}
          value={currentAnswer}
          onChange={(score) => handleAnswer(question.id, score)}
          name={`escala-${question.id}`}
        />
      );
    }

    // Boolean condicional texto (campo 70% + Ok ao lado)
    if (
      question.tipo === "boolean" &&
      question.condicao?.pergunta_adicional?.tipo === "texto"
    ) {
      return (
        <OptionsContainer>
          {question.opcoes.map((opcao) => (
            <OptionButton
              key={opcao.valor}
              selected={currentAnswer === opcao.valor}
              onClick={() => handleAnswer(question.id, opcao.valor)}
            >
              {opcao.texto}
            </OptionButton>
          ))}

          {currentAnswer === "sim" && (
            <ConditionalField>
              <Input
                type="text"
                autoFocus
                placeholder={
                  (question.condicao &&
                    question.condicao.pergunta_adicional &&
                    question.condicao.pergunta_adicional.texto) ||
                  "Digite sua resposta"
                }
                value={conditionalAnswer || ""}
                onChange={(e) =>
                  handleConditionalAnswer(question.id, e.target.value)
                }
                style={{ width: "100%" }}
              />
            </ConditionalField>
          )}
        </OptionsContainer>
      );
    }

    // Boolean condicional numérico (campo 70% + Ok ao lado)
    if (
      question.tipo === "boolean" &&
      question.condicao?.pergunta_adicional?.tipo === "numero"
    ) {
      return (
        <OptionsContainer>
          {question.opcoes.map((opcao) => (
            <OptionButton
              key={opcao.valor}
              selected={currentAnswer === opcao.valor}
              onClick={() => handleAnswer(question.id, opcao.valor)}
            >
              {opcao.texto}
            </OptionButton>
          ))}

          {currentAnswer === "sim" && (
            <ConditionalField>
              <ConditionalFieldRow>
                <SurveyMediumInput
                  type="number"
                  placeholder="Qual o percentual?"
                  value={conditionalAnswer || ""}
                  onChange={(e) =>
                    handleConditionalAnswer(question.id, e.target.value)
                  }
                />
                <SmallOkButton
                  disabled={!conditionalAnswer || conditionalAnswer.trim() === ""}
                  onClick={() => {
                    handleNext();
                  }}
                >
                  Ok
                </SmallOkButton>
              </ConditionalFieldRow>
            </ConditionalField>
          )}
        </OptionsContainer>
      );
    }

    // Numérico geral
    if (question.tipo === "numero") {
      return (
        <div>
          {question.label && <BlockLabel>{question.label}</BlockLabel>}
          <ConditionalFieldRow>
            <NumeroComOpcaoInput
              type="number"
              placeholder={question.placeholder || "Digite sua resposta"}
              value={currentAnswer || ""}
              onChange={(e) => handleAnswer(question.id, e.target.value)}
            />
            {!question.label && (
              <SmallOkButton disabled={!currentAnswer} onClick={handleNext}>
                Ok
              </SmallOkButton>
            )}
          </ConditionalFieldRow>
        </div>
      );
    }

    // CNPJ
    if (question.tipo === "cnpj") {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {question.label && <BlockLabel>{question.label}</BlockLabel>}
          <ConditionalFieldRow>
            <SurveyCompactInput
              type="text"
              placeholder={question.placeholder || "Digite o CNPJ"}
              value={currentAnswer || ""}
              onChange={(e) => {
                setCnpjError(null);
                setCnpjData(null);
                handleAnswer(question.id, formatCNPJ(e.target.value));
              }}
            />
            <SmallOkButton
              disabled={!currentAnswer || cnpjLoading}
              onClick={() => fetchCNPJData(currentAnswer)}
            >
              {cnpjLoading ? "Buscando..." : "Buscar"}
            </SmallOkButton>
          </ConditionalFieldRow>

          {cnpjError && (
            <Text style={{ color: "#c0392b", marginTop: "4px" }}>
              {cnpjError}
            </Text>
          )}

          {cnpjData && (
            <div
              style={{
                marginTop: "6px",
                padding: "12px",
                borderRadius: "8px",
                background: "#f7f7f9",
                border: "1px solid #eee",
                width: "100%",
                maxWidth: "520px",
              }}
            >
              <Text style={{ marginBottom: "6px" }}>
                <strong>Razão Social:</strong> {cnpjData.razao_social || "-"}
              </Text>
              <Text style={{ marginBottom: "6px" }}>
                <strong>Nome Fantasia:</strong> {cnpjData.nome_fantasia || "-"}
              </Text>
              <div style={{ marginTop: "8px" }}>
                <Button variant="primary" onClick={handleNext}>
                  Continuar
                </Button>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Texto
    if (question.tipo === "texto") {
      return (
        <SurveyWideInput
          type="text"
          placeholder={question.placeholder || "Digite sua resposta"}
          value={currentAnswer || ""}
          onChange={(e) => handleAnswer(question.id, e.target.value)}
        />
      );
    }

    // Email obrigatório
    if (question.tipo === "email" && question.obrigatoria) {
      const emailValid = isValidEmail(currentAnswer);

      return (
        <div>
          {question.label && <BlockLabel>{question.label}</BlockLabel>}
          <ValidatedEmailInput
            type="email"
            placeholder={question.placeholder || "Digite sua resposta"}
            value={currentAnswer || ""}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            $valid={emailValid}
          />
        </div>
      );
    }

    if (question.tipo === "email" && !question.obrigatoria) {
      const emailValue = answers[question.id] || "";
      const emailValid = isValidEmail(emailValue);

      return (
        <ValidatedEmailInput
          type="email"
          placeholder={question.placeholder || "Digite sua resposta"}
          value={emailValue}
          onChange={(e) => handleAnswer(question.id, e.target.value)}
          $valid={emailValid}
          maxLength={question.maxLength}
        />
      );
    }

    // Perguntas booleanas gerais
    if (question.tipo === "boolean") {
      return (
        <OptionsContainer>
          {question.opcoes.map((opcao) => (
            <OptionButton
              key={opcao.valor}
              selected={currentAnswer === opcao.valor}
              onClick={() => handleAnswer(question.id, opcao.valor)}
            >
              {opcao.texto}
            </OptionButton>
          ))}

          {question.condicao && currentAnswer === question.condicao.se && (
            <ConditionalField>
              <Text style={{ marginBottom: "10px", fontWeight: "500" }}>
                {question.condicao.pergunta_adicional.texto}
                {question.condicao.pergunta_adicional.obrigatoria && (
                  <RequiredIndicator>*</RequiredIndicator>
                )}
              </Text>
              <ConditionalFieldRow>
                <SurveyHalfInput
                  type="text"
                  placeholder="Digite sua resposta"
                  value={conditionalAnswer || ""}
                  onChange={(e) =>
                    handleConditionalAnswer(question.id, e.target.value)
                  }
                />
                <SmallOkButton
                  disabled={!conditionalAnswer}
                  onClick={handleNext}
                >
                  Ok
                </SmallOkButton>
              </ConditionalFieldRow>
            </ConditionalField>
          )}
        </OptionsContainer>
      );
    }

    // Input padrão
    return (
      <InputContainer>
        <Input
          type={
            question.tipo === "numero"
              ? "number"
              : question.tipo === "email"
              ? "email"
              : "text"
          }
          placeholder={question.placeholder || "Digite sua resposta"}
          value={currentAnswer || ""}
          onChange={(e) => handleAnswer(question.id, e.target.value)}
        />
        <Button
          variant="primary"
          onClick={handleNext}
          disabled={!currentAnswer}
        >
          Enviar resposta
        </Button>
      </InputContainer>
    );
  };

  const currentQuestionData = perguntas[currentQuestion];
  const canAdvance = canAdvanceFromQuestion(currentQuestionData);
  const isLastQuestion = currentQuestion === totalQuestions - 1;
  const lastStepEmailValue =
    isLastQuestion && currentQuestionData?.tipo === "email"
      ? answers[currentQuestionData.id] || ""
      : "";
  const canSubmitEmail =
    !lastStepEmailValue || isValidEmail(lastStepEmailValue);

  if (submitted) {
    return (
      <SurveyContainer>
        <SurveyPageContainer>
          <SurveyCard>
            <SuccessScreen>
              <SuccessEmoji aria-hidden="true">🎉</SuccessEmoji>
              <SuccessTitle>Diagnóstico solicitado com sucesso!</SuccessTitle>
              <SuccessDivider />
              <SuccessMessage>
                Obrigado por responder este autodiagnóstico! 🙌
                <br />
                <br />
                Em pouco tempo enviaremos o <strong>link do resultado</strong> nos
                seus meios de comunicação cadastrados. 📬✨
              </SuccessMessage>
            </SuccessScreen>
          </SurveyCard>
        </SurveyPageContainer>
        <BrandFooterText>Distribuído por Diverti</BrandFooterText>
      </SurveyContainer>
    );
  }

  return (
    <SurveyContainer>
      <SurveyPageContainer>
        <SurveyCard>
          <QuestionNumber>
            {currentQuestion + 1} → Pergunta {currentQuestion + 1} de{" "}
            {totalQuestions}
          </QuestionNumber>

          <QuestionText>
            {currentQuestionData.texto}
            {currentQuestionData.obrigatoria && (
              <RequiredIndicator>*</RequiredIndicator>
            )}
          </QuestionText>

          <QuestionBody>{renderQuestion(currentQuestionData)}</QuestionBody>

          {isLastQuestion && currentQuestionData.tipo === "email" && (
            <SubmitActionsContainer>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={submitting || !canSubmitEmail}
              >
                {submitting ? "Enviando..." : "Solicitar Diagnostico"}
              </Button>
              {submitError && (
                <Text style={{ color: "#c0392b", marginTop: "8px" }}>
                  {submitError}
                </Text>
              )}
            </SubmitActionsContainer>
          )}

          <NavigationContainer>
            <NavigationButtons>
              <NavigationButton
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
              >
                {isPortrait ? "↑" : "←"}
              </NavigationButton>
              {currentQuestion < totalQuestions - 1 && (
                <NavigationButton
                  onClick={handleNext}
                  disabled={!canAdvance}
                >
                  {isPortrait ? "↓" : "→"}
                </NavigationButton>
              )}
            </NavigationButtons>
          </NavigationContainer>
        </SurveyCard>
      </SurveyPageContainer>

      <BrandFooterText>Distribuído por Diverti</BrandFooterText>
    </SurveyContainer>
  );
};

export default SurveyPage;
